import * as React from 'react';
import { connect } from 'react-redux';
import Select from "react-select";
import SimplePagination from './SimplePagination';
import Loader from 'react-loaders';
import { SortByDirection, isSortDirection } from '../../../helpers/graphHelper';
import * as classNames from "classnames";
import { bindActionCreators } from 'redux';
import { IAppState } from '../../../app';
import * as DTOS from "../../../FeralScan.dtos";

export interface ISortOption {
    label: string;
    value: any;
}

export interface ITableColumn {
    className?: string;
    classNameHeader?: string;
    dataKey: string;
    disabled?: boolean;
    label: string;
    onClick?: (d: any) => any;
    render?: (d: any) => any;
}

interface IPaginatedTableProps {
    callbackDirectionChange?: Function;
    callbackPageChange?: Function;
    callbackSortChange?: Function;
    countPerPage: number;
    data: any[];
    dataColumns: ITableColumn[];
    dataLoading: boolean;
    dataSortOptions: ISortOption[];
    defaultPage?: number;
    defaultSort?: ISortOption;
    defaultSortDirection?: ISortOption | SortByDirection;
    hidePaginationControls?: boolean;
    sort: (d, sortBy, sortDirection: SortByDirection) => any;
    tableClassName?: string;

    // user preference
    userPreferenceKey?: string;
    setUserPreference?: (key: string, value: string) => void;
}

interface IPaginatedTableMapped {
    currentUser: DTOS.User;
}

type PaginatedTableOwnProps = IPaginatedTableProps;

type PaginatedTableProps = PaginatedTableOwnProps & IPaginatedTableMapped;

export const SortDirectionOptions: ISortOption[] = [
    { value: SortByDirection.ASC, label: 'Ascending' },
    { value: SortByDirection.DESC, label: 'Descending' },
]

interface PaginatedTableState {
    currentPage: number,
    sortOrder: ISortOption,
    sortDirection: ISortOption
}

class PaginatedTable extends React.Component<PaginatedTableProps, PaginatedTableState> {

    constructor(props, context) {
        super(props, context);

        const { defaultPage, dataSortOptions, defaultSort, defaultSortDirection, userPreferenceKey } = this.props;

        const willSortByPreference: boolean = userPreferenceKey !== undefined;

        // if sort by preference, leave it undefined so we load it in instead
        const order: ISortOption = willSortByPreference || !dataSortOptions ? undefined : defaultSort ? defaultSort : dataSortOptions[0];
        const page: number = defaultPage ? defaultPage : 0;
        var direction: ISortOption;

        if(defaultSortDirection){
            direction = isSortDirection(defaultSortDirection) ? SortDirectionOptions.find(d => d.value === defaultSortDirection) : defaultSortDirection;
        } else {
            direction = SortDirectionOptions[0];
        }

        // don't set a sort order if we are running off user preferences, otherwise default to first sort option
        this.state = {
            currentPage: page,
            sortOrder: order,
            sortDirection: direction
        }

        this.onSortChange = this.onSortChange.bind(this);
        this.onSortDirectionChange = this.onSortDirectionChange.bind(this);
        this.setPage = this.setPage.bind(this);
    }

    componentDidMount() {
        if (this.props.userPreferenceKey && this.props.currentUser && this.props.currentUser.userPreferences) {
            const sortOrder = this.props.currentUser.userPreferences[this.props.userPreferenceKey]
                ? this.props.currentUser.userPreferences[this.props.userPreferenceKey]
                : this.props.dataSortOptions[0].value;

            this.setState({
                sortOrder: this.props.dataSortOptions.find(d => d.value === sortOrder)
            });
        }
    }

    onSortChange(obj) {
        this.setState({
            sortOrder: obj
        });

        this.setUserPreference(obj.value);

        if(this.props.callbackSortChange)
            this.props.callbackSortChange(obj.value);
    }

    onSortDirectionChange(obj) {
        this.setState({
            sortDirection: obj
        });

        if(this.props.callbackDirectionChange)
            this.props.callbackDirectionChange(obj.value);
    }

    setUserPreference(value) {
        if (this.props.setUserPreference !== undefined)
            this.props.setUserPreference(this.props.userPreferenceKey, value);
    }

    setPage(page) {
        this.setState({
            currentPage: page
        })

        if(this.props.callbackPageChange)
            this.props.callbackPageChange(page);
    }

    render() {

        const { data, dataLoading, countPerPage, hidePaginationControls, sort } = this.props;

        const showSortingControls = this.props.dataSortOptions !== undefined;

        // display sortedData if available (i.e there was a way to sort)
        // Otherwise fall back to the raw copyData if there is any
        const copyData = data ? data.slice() : undefined;
        const sortedData = copyData
            ? sort && this.state.sortOrder && this.state.sortDirection
                ? sort(copyData, this.state.sortOrder.value, this.state.sortDirection.value)
                : copyData
            : undefined;

        const paginatedData = sortedData
            ? sortedData.slice((this.state.currentPage * countPerPage), (this.state.currentPage * countPerPage) + countPerPage)
            : undefined;

        const dataRowCount: number = data ? data.length : 0;

        const mobileGapBetweenControls = "mb-1 mb-xl-0";

        return <>
            <div className="row mb-2">
                {
                    showSortingControls ?
                        <>
                            {
                                this.state.sortOrder
                                    ? <Select
                                        className={classNames("col-xl-5 col-lg-8 col-12", mobileGapBetweenControls)}
                                        options={this.props.dataSortOptions}
                                        defaultValue={this.state.sortOrder}
                                        onChange={this.onSortChange}
                                    />
                                    : null
                            }
                            <Select
                                className={classNames("col-xl-2 col-lg-4 col-12 mb-1 mb-lg-0", mobileGapBetweenControls)}
                                options={SortDirectionOptions}
                                defaultValue={this.state.sortDirection}
                                onChange={this.onSortDirectionChange}
                            />
                        </>
                        : undefined
                }
                {
                    // hide if data loading so we don't trigger page changes we don't want
                    // e.g. it will overwrite the default page choice
                    !hidePaginationControls && !dataLoading
                        ? <SimplePagination
                            className={classNames("ml-auto col-auto", mobileGapBetweenControls)}
                            totalRowCount={dataRowCount}
                            numberRowsPerPage={countPerPage}
                            setPage={(p) => this.setPage(p)}
                            currentPage={this.state.currentPage}
                            key="paginate"
                        />
                        : null
                }
            </div>
            {
                dataLoading ? <div className="d-flex justify-content-center mt-5">
                    <Loader type="ball-scale-multiple" active />
                    <div className="ml-4 small">please wait, loading data</div>
                </div>
                    : paginatedData ? <table className={classNames(this.props.tableClassName, "table")}>
                        <thead>
                            <tr>
                                {
                                    this.props.dataColumns.map((column, i) =>
                                        !column.disabled
                                            ? <th
                                                className={classNames(column.classNameHeader)}
                                                key={`dc-${column.dataKey ? column.dataKey : i}`}
                                                scope="col">{column.label}</th>
                                            : null
                                    )
                                }
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.map((row, i) => {
                                // you need to provide an id property for anything you want to render
                                return <tr key={`r-${row.id}`}>
                                    {
                                        this.props.dataColumns.map((column, i) =>
                                            !column.disabled
                                                ? <td
                                                    className={classNames(column.className)}
                                                    key={`dc-${column.dataKey ? column.dataKey : i}`}
                                                    onClick={column.onClick ? () => column.onClick(row) : undefined}
                                                    scope="col">
                                                    {
                                                        column.render ? column.render(row)
                                                            : row[column.dataKey]
                                                    }
                                                </td>
                                                : null
                                        )
                                    }
                                </tr>
                            })
                            }
                        </tbody>
                    </table>
                        : <h2 className="text-center mt-5">No data was returned.</h2>
            }
        </>
    };
}

const mapStateToProps = (state: IAppState, ownProps: PaginatedTableOwnProps) => {

    return {
        currentUser: state.user.currentUser
    }
}

const mapDispatchToProps = (dispatch) => {
    return bindActionCreators({
    }, dispatch)
}

export default connect(mapStateToProps, mapDispatchToProps)(PaginatedTable);